/**
 * Viewing History API Service
 * This service handles communication with the Azure Functions backend for viewing history functionality.
 */

import axios from 'axios';

class ViewingHistoryService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('ViewingHistoryService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get viewing history for a user
   * @param {string} userId - User ID
   * @param {number} limit - Number of items to retrieve (default: 50)
   * @returns {Promise} - API response with viewing history
   */
  async getViewingHistory(userId, limit = 50) {
    try {
      // Clean the URL construction to avoid any double slashes
      const baseUrl = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
      const url = `${baseUrl}/viewinghistory/get?userId=${userId}&limit=${limit}`;
      console.log(`Getting viewing history from: ${url}`);
      
      const response = await axios.get(url);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error fetching viewing history:', error);
      throw error;
    }
  }

  /**
   * Add a video to viewing history
   * @param {Object} historyData - Viewing history data object
   * @returns {Promise} - API response
   */
  async addToHistory(historyData) {
    try {
      // Clean the URL construction to avoid any double slashes
      const baseUrl = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
      const url = `${baseUrl}/viewinghistory/post`;
      console.log(`Adding to viewing history: ${url}`, historyData);
      
      const response = await axios.post(url, historyData);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Video added to history successfully" };
    } catch (error) {
      console.error('Error adding to viewing history:', error);
      throw error;
    }
  }

  /**
   * Clear all viewing history for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async clearHistory(userId) {
    try {
      // Clean the URL construction to avoid any double slashes
      const baseUrl = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
      const url = `${baseUrl}/viewinghistory/deleteall`;
      console.log(`Clearing viewing history: ${url}`);
      
      const response = await axios.delete(url, {
        data: { userId }
      });
      
      return response.data || { message: "Viewing history cleared successfully" };
    } catch (error) {
      console.error('Error clearing viewing history:', error);
      throw error;
    }
  }

  /**
   * Record video watch progress
   * @param {string} userId - User ID
   * @param {string} videoId - Video ID
   * @param {number} watchTime - Watch time in seconds
   * @param {number} totalDuration - Total video duration in seconds
   * @returns {Promise} - API response
   */
  async recordWatchProgress(userId, videoId, watchTime, totalDuration) {
    try {
      const historyData = {
        userId,
        videoId,
        watchTime,
        totalDuration,
        watchPercentage: totalDuration > 0 ? (watchTime / totalDuration) * 100 : 0,
        viewedAt: new Date().toISOString()
      };

      return await this.addToHistory(historyData);
    } catch (error) {
      console.error('Error recording watch progress:', error);
      throw error;
    }
  }

  /**
   * Get recently watched videos (unique videos from history)
   * @param {string} userId - User ID
   * @param {number} limit - Number of unique videos to retrieve
   * @returns {Promise} - API response with unique recently watched videos
   */
  async getRecentlyWatched(userId, limit = 20) {
    try {
      const response = await this.getViewingHistory(userId, limit * 2); // Get more to account for duplicates
      const historyItems = response.data || [];

      // Get unique videos (most recent view per video)
      const uniqueVideos = [];
      const seenVideoIds = new Set();

      for (const item of historyItems) {
        if (!seenVideoIds.has(item.videoId)) {
          seenVideoIds.add(item.videoId);
          uniqueVideos.push(item);
          
          if (uniqueVideos.length >= limit) {
            break;
          }
        }
      }

      return {
        data: uniqueVideos,
        message: "Recently watched videos retrieved successfully",
        count: uniqueVideos.length
      };
    } catch (error) {
      console.error('Error getting recently watched videos:', error);
      throw error;
    }
  }

  /**
   * Get complete viewing history (all items)
   * @param {string} userId - User ID
   * @returns {Promise} - API response with all viewing history
   */
  async getCompleteHistory(userId) {
    try {
      console.log('Getting complete viewing history for user:', userId);
      
      // Get a large number to get all history
      const response = await this.getViewingHistory(userId, 10000);
      
      return {
        ...response,
        message: "Complete viewing history retrieved successfully"
      };
    } catch (error) {
      console.error('Error getting complete viewing history:', error);
      throw error;
    }
  }

  /**
   * Get viewing statistics for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response with viewing statistics
   */
  async getViewingStats(userId) {
    try {
      console.log('Getting viewing statistics for user:', userId);
      
      const response = await this.getCompleteHistory(userId);
      const historyItems = response.data || [];

      // Calculate statistics
      const totalVideosWatched = historyItems.length;
      const uniqueVideos = new Set(historyItems.map(item => item.videoId)).size;
      const totalWatchTime = historyItems.reduce((total, item) => total + (item.watchTime || 0), 0);
      const averageWatchTime = totalVideosWatched > 0 ? totalWatchTime / totalVideosWatched : 0;
      
      // Group by date for viewing patterns
      const viewsByDate = {};
      historyItems.forEach(item => {
        const date = new Date(item.viewedAt).toISOString().split('T')[0];
        viewsByDate[date] = (viewsByDate[date] || 0) + 1;
      });

      return {
        data: {
          totalVideosWatched,
          uniqueVideos,
          totalWatchTime,
          averageWatchTime,
          viewsByDate,
          recentActivity: historyItems.slice(0, 10)
        },
        message: "Viewing statistics retrieved successfully"
      };
    } catch (error) {
      console.error('Error getting viewing statistics:', error);
      throw error;
    }
  }

  /**
   * Search viewing history
   * @param {string} userId - User ID
   * @param {string} searchTerm - Search term
   * @returns {Promise} - API response with filtered history
   */
  async searchHistory(userId, searchTerm) {
    try {
      console.log('Searching viewing history:', { userId, searchTerm });
      
      const response = await this.getCompleteHistory(userId);
      const historyItems = response.data || [];

      // Filter based on search term
      const filteredItems = historyItems.filter(item => 
        (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase()))
      );

      return {
        data: filteredItems,
        message: `Found ${filteredItems.length} items matching "${searchTerm}"`,
        searchTerm
      };
    } catch (error) {
      console.error('Error searching viewing history:', error);
      throw error;
    }
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @returns {Error} - Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || error.response.data || 'Server error';
      return new Error(`API Error: ${message}`);
    } else if (error.request) {
      // Request was made but no response received
      return new Error('Network error: No response from server');
    } else {
      // Something else happened
      return new Error(`Request error: ${error.message}`);
    }
  }
}

const viewingHistoryService = new ViewingHistoryService();
export default viewingHistoryService;
